import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Icon,
  IconButton,
  Separator,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { FaChevronLeft, FaChevronRight, FaStar, FaRegStar } from 'react-icons/fa';
import { MICROSITE_AVALES_CAROUSEL_THRESHOLD } from './MicrositeAvalesBlock.jsx';

function normalizeVariant(variant) {
  if (variant === 'clasico') return 'clasico';
  if (variant === 'elegante') return 'elegante';
  if (variant === 'aurora') return 'aurora';
  return 'moderno';
}

function resenaAvg(resena) {
  const ratings = [
    resena.ratingPuntualidad,
    resena.ratingComunicacion,
    resena.ratingCalidadArtistica,
    resena.ratingGeneral,
  ].filter(Boolean);
  return ratings.length > 0 ? Math.round(ratings.reduce((a, b) => a + b, 0) / ratings.length) : 0;
}

function Stars({ avg, size, filled, empty }) {
  return (
    <HStack gap={0.5}>
      {[...Array(5)].map((_, i) => (
        <Icon key={i} as={i < avg ? FaStar : FaRegStar} w={size} h={size} color={i < avg ? filled : empty} />
      ))}
    </HStack>
  );
}

function ResenasStatsBanner({ stats, normalized, primaryColor, surfaceColor }) {
  if (!stats) return null;

  if (normalized === 'clasico') {
    return (
      <Flex justify="center" mb={6}>
        <HStack gap={4} bg={surfaceColor} px={6} py={3} rounded="lg" border="1px solid" borderColor="gray.200">
          <HStack gap={1}>
            <Text fontSize="xl" fontWeight="800" color={primaryColor}>
              {stats.averageRating}
            </Text>
            <Icon as={FaStar} color="yellow.400" w={5} h={5} />
          </HStack>
          <Separator orientation="vertical" h="24px" />
          <VStack gap={0}>
            <Text fontSize="md" fontWeight="700" color="gray.700">
              {stats.totalReviews}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {stats.totalReviews === 1 ? 'resena' : 'resenas'}
            </Text>
          </VStack>
        </HStack>
      </Flex>
    );
  }

  if (normalized === 'elegante') {
    return (
      <Flex justify="center" mb={8}>
        <HStack gap={6}>
          <HStack gap={1}>
            <Text fontSize="3xl" fontWeight="300" color="gray.800">
              {stats.averageRating}
            </Text>
            <Icon as={FaStar} color="gray.400" w={5} h={5} />
          </HStack>
          <Box w="1px" h="30px" bg="gray.200" />
          <VStack gap={0}>
            <Text fontSize="lg" fontWeight="400" color="gray.700">
              {stats.totalReviews}
            </Text>
            <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="0.08em">
              {stats.totalReviews === 1 ? 'resena' : 'resenas'}
            </Text>
          </VStack>
        </HStack>
      </Flex>
    );
  }

  if (normalized === 'aurora') {
    return (
      <Flex justify="center" mb={8}>
        <HStack gap={6} bg="gray.800" px={8} py={4} rounded="full" shadow="sm">
          <HStack gap={2}>
            <Text fontSize="2xl" fontWeight="800" color={primaryColor}>
              {stats.averageRating}
            </Text>
            <Icon as={FaStar} color="yellow.400" w={6} h={6} />
          </HStack>
          <Separator orientation="vertical" h="30px" />
          <VStack gap={0}>
            <Text fontSize="lg" fontWeight="700" color="gray.300">
              {stats.totalReviews}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {stats.totalReviews === 1 ? 'resena' : 'resenas'}
            </Text>
          </VStack>
        </HStack>
      </Flex>
    );
  }

  return (
    <Flex justify="center" mb={8}>
      <HStack gap={6} bg="white" px={8} py={4} rounded="full" shadow="sm">
        <HStack gap={2}>
          <Text fontSize="2xl" fontWeight="800" color="purple.600">
            {stats.averageRating}
          </Text>
          <Icon as={FaStar} color="yellow.400" w={6} h={6} />
        </HStack>
        <Separator orientation="vertical" h="30px" />
        <VStack gap={0}>
          <Text fontSize="lg" fontWeight="700" color="gray.700">
            {stats.totalReviews}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {stats.totalReviews === 1 ? 'resena' : 'resenas'}
          </Text>
        </VStack>
      </HStack>
    </Flex>
  );
}

function ResenaCard({ resena, normalized }) {
  const avg = resenaAvg(resena);
  const autor = resena.autorCofradia;
  const dateStr = new Date(resena.createdAt).toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric',
  });

  if (normalized === 'elegante') {
    return (
      <Box py={4} borderTop="1px solid" borderColor="gray.100" h="full">
        <VStack gap={2} align="stretch">
          <HStack justify="space-between">
            <VStack align="start" gap={0}>
              <Text fontSize="sm" fontWeight="500">
                {autor?.nombre || 'Anonimo'}
              </Text>
              <Text fontSize="xs" color="gray.400" fontWeight="300">
                {dateStr}
              </Text>
            </VStack>
            <Stars avg={avg} size={3} filled="gray.600" empty="gray.200" />
          </HStack>
          {resena.comment ? (
            <Text fontSize="sm" color="gray.500" fontStyle="italic" fontWeight="300">
              &ldquo;{resena.comment}&rdquo;
            </Text>
          ) : null}
        </VStack>
      </Box>
    );
  }

  if (normalized === 'clasico') {
    return (
      <Box p={5} rounded="lg" border="1px solid" borderColor="gray.200" h="full">
        <VStack gap={2} align="stretch">
          <HStack justify="space-between">
            <HStack gap={2}>
              <Avatar.Root size="xs"><Avatar.Fallback name={autor?.nombre} /><Avatar.Image src={autor?.avatar} /></Avatar.Root>
              <VStack align="start" gap={0}>
                <Text fontSize="xs" fontWeight="600">
                  {autor?.nombre || 'Anonimo'}
                </Text>
                <Text fontSize="10px" color="gray.400">
                  {dateStr}
                </Text>
              </VStack>
            </HStack>
            <Stars avg={avg} size={3} filled="yellow.400" empty="gray.200" />
          </HStack>
          {resena.comment ? (
            <Text fontSize="xs" color="gray.600" fontStyle="italic">
              &ldquo;{resena.comment}&rdquo;
            </Text>
          ) : null}
        </VStack>
      </Box>
    );
  }

  if (normalized === 'aurora') {
    return (
      <Box bg="gray.800" p={6} rounded="xl" shadow="sm" h="full">
        <VStack gap={3} align="stretch">
          <HStack justify="space-between">
            <HStack gap={3}>
              <Avatar.Root size="sm" bg="whiteAlpha.200"><Avatar.Fallback name={autor?.nombre} /><Avatar.Image src={autor?.avatar} /></Avatar.Root>
              <VStack align="start" gap={0}>
                <Text fontSize="sm" fontWeight="600" color="gray.100">
                  {autor?.nombre || 'Anonimo'}
                </Text>
                <Text fontSize="xs" color="gray.400">
                  {dateStr}
                </Text>
              </VStack>
            </HStack>
            <Stars avg={avg} size={3.5} filled="yellow.400" empty="gray.600" />
          </HStack>
          {resena.comment ? (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
              &ldquo;{resena.comment}&rdquo;
            </Text>
          ) : null}
        </VStack>
      </Box>
    );
  }

  return (
    <Box bg="white" p={6} rounded="xl" shadow="sm" h="full">
      <VStack gap={3} align="stretch">
        <HStack justify="space-between">
          <HStack gap={3}>
            <Avatar.Root size="sm" bg="purple.100"><Avatar.Fallback name={autor?.nombre} /><Avatar.Image src={autor?.avatar} /></Avatar.Root>
            <VStack align="start" gap={0}>
              <Text fontSize="sm" fontWeight="600">
                {autor?.nombre || 'Anonimo'}
              </Text>
              <Text fontSize="xs" color="gray.400">
                {dateStr}
              </Text>
            </VStack>
          </HStack>
          <Stars avg={avg} size={3.5} filled="yellow.400" empty="gray.200" />
        </HStack>
        {resena.comment ? (
          <Text fontSize="sm" color="gray.600" fontStyle="italic">
            &ldquo;{resena.comment}&rdquo;
          </Text>
        ) : null}
      </VStack>
    </Box>
  );
}

/**
 * Reseñas publicadas centradas; carrusel si hay más de `MICROSITE_AVALES_CAROUSEL_THRESHOLD` ítems (mismo criterio que avales).
 */
export default function MicrositeResenasBlock({
  resenas,
  stats,
  variant = 'moderno',
  primaryColor = '#7c3aed',
  surfaceColor = 'gray.50',
}) {
  const list = Array.isArray(resenas) ? resenas : [];
  const normalized = normalizeVariant(variant);

  const scrollRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const useCarousel = list.length > MICROSITE_AVALES_CAROUSEL_THRESHOLD;

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !useCarousel) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(max > 4 && el.scrollLeft < max - 4);
  }, [useCarousel]);

  useLayoutEffect(() => {
    updateArrows();
  }, [list.length, useCarousel, updateArrows]);

  useEffect(() => {
    if (!useCarousel) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => updateArrows());
    ro.observe(el);
    window.addEventListener('resize', updateArrows);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateArrows);
    };
  }, [useCarousel, updateArrows]);

  const scrollByDir = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = Math.min(el.clientWidth * 0.75, 360) * dir;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  if (list.length === 0) return null;

  return (
    <>
      <ResenasStatsBanner
        stats={stats}
        normalized={normalized}
        primaryColor={primaryColor}
        surfaceColor={surfaceColor}
      />
      {!useCarousel ? (
        <Flex justify="center" align="stretch" flexWrap="wrap" gap={{ base: 4, md: 5 }} w="full">
          {list.map((resena) => (
            <Box
              key={resena.id}
              flex={{ base: '1 1 100%', md: '0 1 calc(50% - 12px)' }}
              maxW={{ base: '100%', md: '420px' }}
              minW={{ md: '260px' }}
            >
              <ResenaCard resena={resena} normalized={normalized} />
            </Box>
          ))}
        </Flex>
      ) : (
        <Box w="full" maxW="100%">
          <Flex align="center" gap={{ base: 0, md: 2 }} w="full" justify="center">
            <IconButton
              display={{ base: 'none', md: 'flex' }}
              aria-label="Reseña anterior"
              variant="ghost"
              size="md"
              flexShrink={0}
              onClick={() => scrollByDir(-1)}
              disabled={!canPrev}
              colorPalette="purple"><FaChevronLeft /></IconButton>
            <Box
              ref={scrollRef}
              onScroll={updateArrows}
              overflowX="auto"
              overflowY="hidden"
              flex="1"
              maxW={{ base: '100%', md: 'calc(100% - 88px)' }}
              py={1}
              px={{ base: 2, md: 1 }}
              css={{
                scrollSnapType: 'x mandatory',
                '& scrollPaddingLeft': { base: '16px', md: '24px' },
                '& scrollPaddingRight': { base: '16px', md: '24px' },
                WebkitOverflowScrolling: 'touch',
                '& &::-webkit-scrollbar': { height: '6px' },

                '& &::-webkit-scrollbar-thumb': {
                  background: 'var(--chakra-colors-purple-200)',
                  borderRadius: 'full',
                }
              }}
            >
              <HStack gap={5} align="stretch" w="max-content" mx="auto" px={{ base: 1, md: 2 }}>
                {list.map((resena) => (
                  <Box
                    key={resena.id}
                    flex="0 0 auto"
                    w={{ base: 'min(88vw, 360px)', md: '340px' }}
                    maxW="400px"
                    scrollSnapAlign="center"
                  >
                    <ResenaCard resena={resena} normalized={normalized} />
                  </Box>
                ))}
              </HStack>
            </Box>
            <IconButton
              display={{ base: 'none', md: 'flex' }}
              aria-label="Reseña siguiente"
              variant="ghost"
              size="md"
              flexShrink={0}
              onClick={() => scrollByDir(1)}
              disabled={!canNext}
              colorPalette="purple"><FaChevronRight /></IconButton>
          </Flex>
        </Box>
      )}
    </>
  );
}
